import React from "react";

/**
 * Icon-only rendering of the PRISM AI logo.
 *
 * The source SVG (`prosm-logo.svg`) bakes the "PRISM AI" wordmark inside
 * the same 504×504 viewBox as the icon. That works at large sizes (e.g.
 * marketing, favicon) but at 42–72 px the wordmark collapses to ~4 px tall
 * and becomes illegible while also stealing space from the icon itself.
 *
 * This component renders the same vectors inline with a tighter viewBox
 * (`90 60 310 310`) that excludes the wordmark, so the round mark + sail
 * fill the box edge-to-edge at any size.
 *
 * Use this anywhere the wordmark isn't needed — e.g. message avatars, the
 * welcome-screen badge, the loading state. Keep the original
 * `prosm-logo.svg` for the favicon and any place that explicitly wants the
 * wordmark.
 */
const PrismLogoIcon = ({ size = 42, title = "PRISM AI", className, style }) => {
  const dim = typeof size === "number" ? `${size}px` : size;
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={dim}
      height={dim}
      viewBox="90 60 310 310"
      role="img"
      aria-label={title}
      className={className}
      style={{ display: "block", flexShrink: 0, ...style }}
    >
      <title>{title}</title>
      {/* Round mark (deep purple) */}
      <path
        fill="#351049"
        fillRule="evenodd"
        d="M378.19,212.06c-1.1-73.29-60.85-132.38-134.4-132.38s-133.98,59.74-134.42,133.61c0,.17.06.31.15.45,1.1,73.29,60.85,132.38,134.4,132.38s133.98-59.74,134.43-133.61c0-.17-.06-.32-.15-.45Z"
      />
      {/* White underlines beneath the sail */}
      <path
        fill="#fff"
        fillRule="evenodd"
        d="M305.78,303.09h-61.87s-61.87,0-61.87,0c-3.65,0-6.61-2.96-6.61-6.61h0c0-3.65,2.96-6.61,6.61-6.61h61.87s61.87,0,61.87,0c3.65,0,6.61,2.96,6.61,6.61h0c0,3.65-2.96,6.61-6.61,6.61Z"
      />
      <path
        fill="#fff"
        fillRule="evenodd"
        d="M289.6,324.63h-45.27s-45.27,0-45.27,0c-3.65,0-6.61-2.96-6.61-6.61h0c0-3.65,2.96-6.61,6.61-6.61h45.27s45.27,0,45.27,0c3.65,0,6.61,2.96,6.61,6.61h0c0,3.65-2.96,6.61-6.61,6.61Z"
      />
      {/* Light-cyan sail body */}
      <path
        fill="#5ccbf5"
        d="M241.48,97.38l-100.97,157.94c-1.18,1.85-.07,4.31,2.1,4.64l101.19,15.45,101.42-15.49c2.06-.32,3.12-2.65,2-4.41l-101.09-158.14c-1.08-1.7-3.56-1.7-4.64,0Z"
      />
      {/* Sail spine + outline + crease lines */}
      <line
        stroke="#351049"
        strokeMiterlimit="10"
        fill="none"
        x1="243.8"
        y1="98.35"
        x2="243.8"
        y2="273.2"
      />
      <path
        stroke="#fff"
        strokeMiterlimit="10"
        fill="none"
        d="M243.22,98.95l-100.42,157.09c-.32.5-.02,1.17.57,1.26l100.08,15.93c.24.04.48.04.71,0l100.06-15.93c.59-.09.9-.77.57-1.27l-100.41-157.08c-.27-.42-.89-.42-1.16,0Z"
      />
      <path
        stroke="#351049"
        strokeMiterlimit="10"
        fill="none"
        d="M243.93,235.33l-100.31,21.8c-.08.04-.06.16.03.17l100.24,16.06,100.22-16.05c.1-.02.12-.15.03-.19l-100.2-21.79Z"
      />
      <line
        stroke="#fff"
        strokeMiterlimit="10"
        fill="none"
        x1="243.81"
        y1="98.35"
        x2="224.58"
        y2="270.19"
      />
      <line
        stroke="#fff"
        strokeMiterlimit="10"
        fill="none"
        x1="243.8"
        y1="98.35"
        x2="263.03"
        y2="270.19"
      />
      <path
        stroke="#351049"
        strokeMiterlimit="10"
        fill="none"
        d="M143.62,257.19c-.08.04-.06.16.03.17l100.24,16.06,100.22-16.05c.1-.02.12-.15.03-.19"
      />
    </svg>
  );
};

export default PrismLogoIcon;
